import { createGenericFormStructureFromModelMetadata, DynamicFormArray, DynamicFormControl, DynamicFormGroup } from '../dynamic-form';
import { DynamicFormControlType } from '../models/dynamic-form-group-field';
import { createValidatorsForField, prepareValidationMetadata, addValidatorsToFieldDefinition, getNgValidatorsFromConfig } from './build-validators';
import { ClassTransformerMetadataHelpers, isPrimitiveClass } from '../utils';
import { CLASS_VALIDATOR_GLOBAL_CONFIG_FORMBUILDER } from '@core/shared/model';
// works similar to angulars FormBuilder._reduceControls method
// but builds controls based on model metadata and passed formStructure.
function buildControls(formBuilder, factoryModel, meta,
// meta includes info for all properties known by cass-transformer
formStructure,
// keys define which controls will be created, values specify default control value
config) {
  if (!formBuilder) throw new Error('buildControls received no dfb instance!');
  // Get the validation rules from the object decorators
  const validationDeps = prepareValidationMetadata(factoryModel, config.classValidatorOptions);
  const {
    formGroupFields
  } = validationDeps;
  meta.properties.forEach(propMeta => {
    const shouldCreateControl = shouldCreateControlForMetaProp(formStructure, propMeta);
    // console.log('shouldCreateControl for '+propMeta.propertyName,shouldCreateControl)
    if (!shouldCreateControl) return;
    const fieldDefinition = createFieldDefinition(formGroupFields, propMeta, formStructure);
    const {
      validationDefinitions,
      validationFunctions,
      fieldValidations
    } = createValidatorsForField(validationDeps, fieldDefinition, formStructure, config.classValidatorOptions, config.parentConditionalValidators);
    addValidatorsToFieldDefinition(fieldDefinition, validationDefinitions, validationFunctions, fieldValidations);
    if (fieldDefinition.controlType === DynamicFormControlType.primitive) {
      // simple formcontrol
      formGroupFields[propMeta.propertyName] = new DynamicFormControl(fieldDefinition);
    } else {
      const options = {
        // conditional validation checks to be passed to children.
        parentConditionalValidators: fieldValidations.conditional,
        classValidatorOptions: config.classValidatorOptions
      };
      // sub-structure - formArray or formGroup
      switch (fieldDefinition.controlType) {
        case DynamicFormControlType.object:
          formGroupFields[propMeta.propertyName] = buildDynamicFormGroup(formBuilder, fieldDefinition, formStructure[fieldDefinition.name], options);
          break;
        case DynamicFormControlType.primitiveArray:
          const controls = fieldDefinition.defaultValue.map(value => {
            return new DynamicFormControl({
              ...fieldDefinition,
              data: value
            });
          });
          formGroupFields[propMeta.propertyName] = new DynamicFormArray(formBuilder, {
            ...fieldDefinition,
            data: controls
          }, options);
          break;
        case DynamicFormControlType.objectArray:
          const controls2 = fieldDefinition.defaultValue.map(item => {
            return buildDynamicFormGroup(formBuilder, fieldDefinition, item, options);
          });
          formGroupFields[propMeta.propertyName] = new DynamicFormArray(formBuilder, {
            ...fieldDefinition,
            data: controls2
          }, options);
          //   console.log('///////// FormGroup for formArray',newFormArrayGroup,newControlsConfig[key][0].constructor)
          break;
        default:
          throw new Error('Detected unknown controlType for field ' + fieldDefinition.name + ': ' + fieldDefinition.controlType);
      }
    }
  });
  // console.log('formGroupFields',factoryModel,formGroupFields)
  return formGroupFields;
}
/**
 * @prop formStructure acts as a blueprint:
 * keys define which FormControls will be created,
 * values specify default values for FormControls
 * fallback: if no formStructure was passed, generate one based on class-transform plainToInstance
 */
export function buildDynamicFormGroup(dfb, definitionOrType, formStructureInput, config) {
  if (!config) config = {};
  if (!config.classValidatorOptions) config.classValidatorOptions = CLASS_VALIDATOR_GLOBAL_CONFIG_FORMBUILDER;
  if (!definitionOrType) throw new Error('buildDynamicFormGroup called with falsy definitionOrType');
  let definition = null;
  let factoryModel;
  let updateOn;
  if (definitionOrType['type']) {
    definition = {
      validationFunctions: []
    };
    factoryModel = definitionOrType['type'];
    // as no FieldDefinition was received, try to transform possible legacy / raw angular validators
    let ngCtrlConfig = getNgValidatorsFromConfig(config);
    updateOn = ngCtrlConfig.updateOn;
    if (ngCtrlConfig.validators) definition.validationFunctions.push(...ngCtrlConfig.validators.filter(validator => validator).map(validator => {
      return {
        type: 'sync',
        validator
      };
    }));
    if (ngCtrlConfig.asyncValidators) definition.validationFunctions.push(...ngCtrlConfig.asyncValidators.filter(validator => validator).map(validator => {
      return {
        type: 'async',
        validator
      };
    }));
  } else {
    definition = definitionOrType;
    factoryModel = definition.meta.classType;
  }
  // If there is no manual controlsConfig specified, use given factoryModel to create a proto-form-model. 
  const meta = ClassTransformerMetadataHelpers.getMetadata(factoryModel, config, 0, [], true);
  // console.log('dfb dynamicGroup meta...', factoryModel, meta, options)
  // Create an Angular group from the top-level object
  const generatedStructure = createGenericFormStructureFromModelMetadata(factoryModel, null, config.classTransformOptions);
  const formStructure = formStructureInput || generatedStructure;
  const controls = buildControls(dfb, factoryModel, meta, formStructure, config);
  // Initialize the resulting group
  const dynamicFormGroup = new DynamicFormGroup(factoryModel, meta, config, definition, formStructure, updateOn);
  Object.keys(controls).forEach(key => {
    dynamicFormGroup.addControl(key, controls[key]);
  });
  // console.log('>>> dynamicFormGroup', formStructureInput,generatedStructure, dynamicFormGroup.factoryModel, dynamicFormGroup)
  // Add a listener to the dynamic group for value changes; on change, execute validation
  dynamicFormGroup.subscribeToValueChanges(undefined, config?.classValidatorOptions);
  return dynamicFormGroup;
}
export function buildPrimitiveArrayControls(values, fieldDefinition, options) {
  return values.map((newControlsConfigItem, i) => {
    return new DynamicFormControl({
      ...fieldDefinition,
      data: newControlsConfigItem
    });
  });
}
export function shouldCreateControlForMetaProp(defaultValue, metadataItem) {
  return Object.keys(defaultValue || {}).length === 0 || Object.getOwnPropertyDescriptor(defaultValue || {}, metadataItem.propertyName) || metadataItem.isArray;
}
function createFieldDefinition(formGroupFields, propMeta, fields) {
  const defaultValue = fields?.[propMeta.propertyName];
  const fieldDefinition = {
    name: propMeta.propertyName,
    meta: propMeta,
    data: formGroupFields[propMeta.propertyName] || undefined,
    defaultValue,
    validationFlags: {
      conditional: false,
      parentConditional: false,
      nested: false,
      custom: false
    },
    validationFunctions: [],
    validationDefinitions: [],
    controlType: getControlType(propMeta, defaultValue)
  };
  if (fieldDefinition.data === undefined) fieldDefinition.data = defaultValue;
  // TRY LINK EXISTS NATIVE VALIDATIONS, UNSTABLE !!!
  if (Array.isArray(fieldDefinition.data) && fieldDefinition.data.length > 1 && fieldDefinition.data.filter((validationFunction, index) => index > 0 && typeof validationFunction === 'function').length > 0) {
    fieldDefinition.data.filter((validationFunction, index) => index > 0 && typeof validationFunction === 'function').forEach(validationFunction => fieldDefinition.validationFunctions.push(validationFunction));
    fieldDefinition.data = fieldDefinition.data[0];
  }
  return fieldDefinition;
}
function getControlType(metadataItem, defaultValue = undefined) {
  // console.log('getControlType',metadataItem, defaultValue)
  // if (
  // 	Object.keys(defaultValue || {}).length === 0 ||
  // 	Object.getOwnPropertyDescriptor(
  // 		defaultValue || {},
  // 		metadataItem.propertyName
  // 	) ||
  // 	metadataItem.isArray
  // ) {
  if (!metadataItem.isArray) {
    if (!metadataItem.classType || isPrimitiveClass(metadataItem.classType)) {
      return DynamicFormControlType.primitive;
    } else {
      return DynamicFormControlType.object;
    }
  } else {
    if (!metadataItem.classType || isPrimitiveClass(metadataItem.classType)) {
      return DynamicFormControlType.primitiveArray;
    } else {
      return DynamicFormControlType.objectArray;
    }
  }
  // }
}
export function createSubFormGroups(formBuilder, newControlsConfig, meta, extra, asyncValidators, updateOn, validators) {
  Object.keys(newControlsConfig).forEach(key => {
    // console.log('createSubFormGroups?',key,canCreateGroup(newControlsConfig?.[key]),canCreateArray(newControlsConfig?.[key]));
    if (canCreateGroup(newControlsConfig?.[key])) {
      // recursively create a dynamic group for the nested object
      newControlsConfig[key] = formBuilder.group(newControlsConfig[key].constructor, undefined, {
        ...(extra.classValidatorOptions ? {
          classValidatorOptions: extra.classValidatorOptions
        } : {}),
        asyncValidators,
        updateOn,
        validators
      });
    } else if (canCreateArray(newControlsConfig?.[key])) {
      if (newControlsConfig[key][0].constructor) {
        // recursively create an array with a group
        const newFormArrayGroup = newControlsConfig[key].map(newControlsConfigItem => this.group(newControlsConfigItem.constructor, undefined, {
          ...(extra.classValidatorOptions ? {
            classValidatorOptions: extra.classValidatorOptions
          } : {}),
          asyncValidators,
          updateOn,
          validators
        }));
        //   console.log('///////// FormGroup for formArray',newFormArrayGroup,newControlsConfig[key][0].constructor)
        newControlsConfig[key] = formBuilder.array(newFormArrayGroup);
        //   console.log('///////// formArray',newControlsConfig[key])
      } else {
        // Create an array of form controls
        newControlsConfig[key] = formBuilder.array(newControlsConfig[key].map(newControlsConfigItem => this.control(newControlsConfigItem)));
      }
    }
  });
}
function canCreateGroup(candidate) {
  return candidate && !Array.isArray(candidate) && candidate.constructor && typeof candidate === 'object' && (candidate.length === undefined || candidate.length !== undefined && Object.keys(candidate).length === candidate.length);
}
function canCreateArray(candidateArray) {
  if (Array.isArray(candidateArray) === false) {
    return false;
  }
  const candidate = candidateArray[0];
  return candidate.constructor && typeof candidate === 'object' && (candidate.length === undefined || candidate.length !== undefined && Object.keys(candidate).length === candidate.length);
}